import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { BENTOBOX_INSTAGRAM_MEDIA_ENDPOINT } from 'reservations/base/constants/endpoints';
import { DEFAULT_QUERY_OPTIONS } from 'reservations/base/constants/queryOptions';
import { buildBentoboxApiEndpoint } from 'reservations/base/helpers/buildBentoboxApiEndpoint';
import { RequestError, restClient } from 'utility/restClient';

const InstagramMedia = z.object({
  media_url: z.string()
});

const SelectInstagramMedia = z.object({
  mediaUrl: z.string()
});

const InstagramMediaResponse = z.object({
  instagram_name: z.string(),
  media: z.array(InstagramMedia),
  connected: z.boolean().default(true)
});

type InstagramMediaResponse = z.infer<typeof InstagramMediaResponse>;

const SelectInstagramMediaResponse = z.object({
  instagramName: z.string(),
  media: z.array(SelectInstagramMedia),
  connected: z.boolean().default(true)
});

export type SelectInstagramMediaResponse = z.infer<
  typeof SelectInstagramMediaResponse
>;

async function fetchInstagramMedia() {
  const endpoint = buildBentoboxApiEndpoint(BENTOBOX_INSTAGRAM_MEDIA_ENDPOINT);

  try {
    const response = await restClient.get(endpoint);

    return InstagramMediaResponse.parse(response);
  } catch (_err) {
    /* istanbul ignore next */
    return {
      instagram_name: '',
      media: [],
      connected: false
    };
  }
}

export function useInstagramMediaQuery() {
  return useQuery<
    InstagramMediaResponse,
    RequestError,
    SelectInstagramMediaResponse
  >({
    ...DEFAULT_QUERY_OPTIONS,
    queryKey: ['instagram-media-query'],
    queryFn: fetchInstagramMedia,
    select: data => ({
      instagramName: data.instagram_name,
      media: data.media.map(media => ({ mediaUrl: media.media_url })),
      connected: data.connected
    })
  });
}
